import React, { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Plus } from 'lucide-react';
import { EASE_OUT } from '../../lib/motion';
import Button from '../primitives/Button';
import Label from '../primitives/Label';
import Accent from './Accent';

const rise = delay => ({
  initial: { opacity: 0, y: 24, filter: 'blur(8px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.9, ease: EASE_OUT, delay } },
  exit: { opacity: 0, transition: { duration: 0.3 } }
});

/*
 * Inside a world, after the dive: the service's promise, features and headline result,
 * with the way on to its full page or back out to the core (Escape and browser Back too).
 */
const LabWorld = ({ world, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
    const onKey = e => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <motion.div className="absolute inset-0 z-20 flex items-end px-[var(--gutter)] pb-[clamp(4.5rem,10vh,7rem)] lg:items-center lg:pb-0" exit={{ opacity: 0, transition: { duration: 0.35 } }}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent lg:bg-gradient-to-r lg:from-background/90 lg:via-background/45" />
      <section ref={ref} tabIndex={-1} aria-label={`${world.name} world`} className="relative w-full max-w-[36rem] outline-none lg:ml-[10rem]">
        <motion.div {...rise(0)}>
          <Label index={world.index}>{world.label}</Label>
        </motion.div>
        <motion.h2 {...rise(0.08)} className="mt-5 text-balance text-[clamp(2.1rem,4.2vw,4.25rem)] font-[440] leading-[1] tracking-[-0.04em] [font-stretch:110%]">
          <Accent text={world.title} />
        </motion.h2>
        <motion.ul {...rise(0.18)} className="mt-7 divide-y divide-border/60 border-y border-border/60">
          {world.features.map(feature => (
            <li key={feature} className="flex items-center gap-3 py-3 text-[0.9375rem]">
              <Plus size={14} className="shrink-0 text-brand" aria-hidden="true" />
              {feature}
            </li>
          ))}
        </motion.ul>
        {world.highlight && (
          <motion.p {...rise(0.26)} className="mt-7 flex items-baseline gap-4">
            <span className="accent-brand text-[clamp(2.5rem,4vw,3.5rem)] font-[450] leading-none tracking-[-0.04em] [font-stretch:112%]">{world.highlight.value}</span>
            <span className="text-[0.9375rem] text-muted-foreground">
              {world.highlight.label}
              <span className="label mt-1 block">{world.highlight.context}</span>
            </span>
          </motion.p>
        )}
        <motion.div {...rise(0.34)} className="mt-9 flex flex-wrap gap-3">
          <Button to={world.path} arrow magnetic>
            Open the {world.name} page
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Back to the core
          </Button>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default LabWorld;
